import React, { useEffect, useState } from 'react';
import {
  Keyboard,
  Linking,
  Platform,
  TouchableWithoutFeedback,
} from 'react-native';
import analytics from '@react-native-firebase/analytics';

import { Button } from '../../components/Interface/Forms/Buttons/Button';
import { SupportTypeBottomSheet } from '../../components/shared/SupportTypeBottomSheet';

import {
  Container,
  Content,
  Footer,
  HelpArticleList,
  HelpVideoContainer,
  HelpVideoSubtitle,
  HelpVideoThumb,
  HelpVideoTitle,
  SupportContactIcon,
  SupportContactItem,
  SupportContactList,
  SupportContactText,
  SupportContactTitle,
  TutorialsButtonContainer,
  VideoInfos,
} from './styles';

import { useNavigation } from '@react-navigation/native';
import { Header as HeaderButton } from '../../components/Interface/Headers/Header';
import { HelpArticleItem } from '../../components/shared/HelpArticleItem';

interface SearchArticle {
  id: string;
  text: string;
  data: {
    title: string;
    text: string;
    buttonAction?: () => void;
    buttonTitle?: string;
    content?: {
      title?: string;
      text?: string;
    }[];
  };
}

import thumbVideo from '../../assets/images/banners/thumb-suporte.webp';
import { useTheme } from 'styled-components/native';

import { SearchInput } from '../../components/Interface/Forms/Inputs/SearchInput';
import { NotFound } from '../../components/Interface/Search/NotFound';
import { HelpVideo } from '../../components/shared/HelpVideo';
import { HelpArticleDTO } from '../../dtos/HelpArticleDTO';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRemoteConfig } from '../../context/useRemoteConfig';

export function Help() {
  const navigation = useNavigation();

  const theme = useTheme();

  const { supportHelpText } = useRemoteConfig(true);

  const [showVideo, setShowVideo] = useState(false);
  const [showSupportBottomSheet, setShowSupportBottomSheet] = useState(false);

  const [filteredArticles, setFilteredArticles] = useState<SearchArticle[]>(
    [] as SearchArticle[],
  );
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    analytics().logEvent('tela_ajuda');
  }, []);

  const supportTitle =
    supportHelpText.find(item => item.type === 'title')?.value ?? '';
  const supportHours = supportHelpText
    .filter(item => item.type === 'hour')
    .map(item => item.value);
  const supportPhone =
    supportHelpText.find(item =>
      ['phone', 'whatsapp', 'phone_number'].includes(item.type),
    )?.value ?? '';
  const supportPhoneDigits = supportPhone.replace(/\D/g, '');
  const supportPhoneLink = supportPhoneDigits
    ? `https://api.whatsapp.com/send/?phone=${supportPhoneDigits}&text&type=phone_number&app_absent=0`
    : '';

  const articleData: SearchArticle[] = [
    {
      id: '1',
      text: 'Como funciona o reembolso?',
      data: {
        title: 'Como funciona o reembolso?',
        text: 'Você pode solicitar o reembolso direto pelo app. Depois do envio, nossa equipe analisa e você acompanha o status na tela de reembolsos.\n\nPor segurança, o reembolso só pode ser realizado para o mesmo CPF que efetuou o pagamento.\n\nEssa regra segue as normas do Banco Central e existe para evitar fraudes e garantir que o valor seja devolvido corretamente ao titular da compra.',
        buttonAction: () => navigation.navigate('Reembolsos'),
        buttonTitle: 'Ver reembolsos',
      },
    },
    {
      id: '2',
      text: 'Qual a diferença entre as categorias Super Barato e Barato?',
      data: {
        title: 'Qual a diferença entre as categorias Super Barato e Barato?',
        text: 'Os postos classificados na categoria Super Barato oferecem mais descontos nos valores dos combustíveis que os postos da categoria Barato.',
      },
    },
    {
      id: '3',
      text: 'Como funciona a compra por cartão de crédito?',
      data: {
        title: 'Como funciona a compra por cartão de crédito?',
        text: 'É possível cadastrar até 3 cartões, é importante ressaltar que os cartões cadastrados devem ser do titular da conta. Acesse a aba "cartões", clique em "adicionar novo" e insira os dados solicitados.',
      },
    },
    {
      id: '4',
      text: 'Quantos cartões de crédito posso cadastrar? Posso cadastrar um cartão de crédito de terceiros?',
      data: {
        title:
          'Quantos cartões de crédito posso cadastrar? Posso cadastrar um cartão de crédito de terceiros?',
        text: 'Você pode cadastrar até 3 cartões, o cartão precisa ser do titular da conta. Acesse a aba “cartões”, clique em “adicionar novo” e insira os dados solicitados.',
      },
    },
    {
      id: '5',
      text: 'Como excluir um cartão de crédito?',
      data: {
        title: 'Como excluir um cartão de crédito?',
        text: 'Acesse a aba “cartões”, selecione o cartão que você quer excluir e clique em “remover forma de pagamento”.',
      },
    },
    {
      id: '6',
      text: 'Quais são os limites de compra?',
      data: {
        title: 'Quais são os limites de compra?',
        text: 'Os limites são atualizados pelo sistema conforme a quantidade de compras realizadas e podem ser alterados sem aviso prévio.',
      },
    },
    {
      id: '7',
      text: 'Em quanto tempo o QR Code de combustível fica disponível após o pagamento?',
      data: {
        title:
          'Em quanto tempo o QR Code de combustível fica disponível após o pagamento?',
        text: 'A liberação é imediata após a confirmação de pagamento.',
      },
    },
    {
      id: '8',
      text: 'Existe taxa?',
      data: {
        title: 'Existe taxa?',
        text: 'Sim. No PIX, há a cobrança de uma taxa pelo serviço. No cartão de crédito, a taxa é cobrada de acordo com a quantidade de parcelas.',
        content: [
          {
            title: 'Sobre a Taxa de Serviço da Baratão:',
            text: 'A Baratão se dedica incansavelmente a conectar você aos melhores preços de combustíveis, produtos e serviços automotivos. A taxa de serviço que você vê no checkout é mais do que apenas um número; ela é o que nos permite fazer a diferença no seu dia a dia. Lembre-se, a cada litro abastecido, você não apenas economiza, mas também contribui para o fortalecimento e crescimento da Baratão, permitindo-nos continuar negociando as melhores condições e preços para você.',
          },
          {
            title: 'Aqui está o porquê dessa taxa:',
          },
          {
            text: 'Negociação Exclusiva: Nossa equipe trabalha continuamente para negociar descontos significativos diretamente com os postos, garantindo que você sempre tenha acesso aos melhores preços.',
          },
          {
            text: 'Conexão Direta: Facilitamos a conexão entre donos de postos e clientes como você, proporcionando uma experiência de abastecimento suave e econômica.',
          },
          {
            text: 'Marketing e Manutenção: Investimos em marketing para alcançar mais usuários e melhorar a manutenção do nosso sistema, garantindo a segurança e a eficiência de suas transações.',
          },
          {
            text: 'Melhorias constantes: Estamos sempre aprimorando nossa plataforma para oferecer novas funcionalidades e uma experiência ainda mais amigável.',
          },
          {
            text: 'Clube de Vantagens: Oferecemos um clube exclusivo com mais de 10.000 lojas parceiras, trazendo descontos e benefícios em uma variedade de produtos e serviços.',
          },
        ],
      },
    },
    {
      id: '9',
      text: 'Como usar um cupom de desconto?',
      data: {
        title: 'Como usar um cupom de desconto?',
        text: 'Na aba de pagamento, clique em "aplicar novo cupom de desconto" e digite o cupom. Após inserir o código clique em aplicar, verifique quais postos que aceitam este cupom e clique em confirmar.',
      },
    },
    {
      id: '10',
      text: 'Comprei o cupom da categoria/região errada, e agora?',
      data: {
        title: 'Comprei o cupom da categoria/região errada, e agora?',
        text: 'Basta solicitar o reembolso pelo app e realizar nova compra na categoria/região desejada.',
        buttonAction: () => navigation.navigate('Reembolsos'),
        buttonTitle: 'Solicitar reembolso',
      },
    },
    {
      id: '11',
      text: 'Qual a validade do cupom?',
      data: {
        title: 'Qual a validade do cupom?',
        text: 'O cupom possui 7 dias de validade, vencendo às 23:59h do sétimo dia. Você pode conferir a validade no próprio cupom.',
      },
    },
  ];

  function handleHelpArticle(data: HelpArticleDTO) {
    analytics().logEvent('tela_ajuda_clica_artigo');
    navigation.navigate('HelpArticle', { data });
  }

  function handleSearchArticles(text: string) {
    const filter = articleData.filter(
      (e: SearchArticle) =>
        e.text
          .toLowerCase()
          .normalize('NFD')
          .includes(text.toLowerCase().normalize('NFD')) ||
        e.data.text
          .toLowerCase()
          .normalize('NFD')
          .includes(text.toLowerCase().normalize('NFD')) ||
        e.data.title
          .toLowerCase()
          .normalize('NFD')
          .includes(text.toLowerCase().normalize('NFD')),
    );

    setFilteredArticles(filter);
    setSearchTerm(text);
  }

  return (
    <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
      <TouchableWithoutFeedback
        onPress={() => Platform.OS !== 'web' && Keyboard.dismiss()}
        accessible={false}>
        <Container>
          <HelpVideoContainer
            onPress={() => setShowVideo(true)}
            activeOpacity={1}>
            <HelpVideoThumb source={thumbVideo}>
              <HeaderButton
                icon="chevron-left"
                statusBarLight
                color={theme.colors.shape}
              />
              <VideoInfos>
                <HelpVideoTitle>Como comprar no Baratão?</HelpVideoTitle>
                <HelpVideoSubtitle>
                  Clique e aprenda a utilizar o Baratão
                </HelpVideoSubtitle>
              </VideoInfos>
            </HelpVideoThumb>
          </HelpVideoContainer>
          <Content>

            <SearchInput
              placeholder="Digite sua dúvida aqui"
              value={searchTerm}
              onChangeText={text => handleSearchArticles(text)}
            />

            <HelpArticleList showsVerticalScrollIndicator={false}>
              {!searchTerm ? (
                articleData.map((item, index) => (
                  <HelpArticleItem
                    key={item.id}
                    showBorder={index === 0 ? false : true}
                    text={item.text}
                    action={() => handleHelpArticle(item.data)}
                  />
                ))
              ) : !filteredArticles.length ? (
                <NotFound />
              ) : (
                filteredArticles.map((item, index) => (
                  <HelpArticleItem
                    key={item.id}
                    showBorder={index === 0 ? false : true}
                    text={item.text}
                    action={() => handleHelpArticle(item.data)}
                  />
                ))
              )}


        {
          Platform.OS !== 'web' && (
                  <TutorialsButtonContainer>
                <Button
                  enabled
                  variant="outline"
                  title="Ver tutoriais do app"
                  onPress={() => navigation.navigate('Tutoriais')}
                />
              </TutorialsButtonContainer>
          )
        }
            </HelpArticleList>

          </Content>

          <Footer>
            {(supportTitle || supportHours.length || supportPhone) && (
              <SupportContactList>
                {supportTitle ? (
                  <SupportContactTitle>{supportTitle}</SupportContactTitle>
                ) : null}
                {supportHours.length ? (
                  <SupportContactItem style={{ flexDirection: 'column' }}>
                    {supportHours.map((item, index) => (
                      <SupportContactText key={`${item}-${index}`}>
                        {item}
                      </SupportContactText>
                    ))}
                  </SupportContactItem>
                ) : null}
                {supportPhone ? (
                  <SupportContactItem>
                    <SupportContactIcon name="whatsapp" />
                    <SupportContactText>{supportPhone}</SupportContactText>
                  </SupportContactItem>
                ) : null}
              </SupportContactList>
            )}
            {supportPhoneLink ? (
              <Button
                enabled
                title="Chat com atendente"
                onPress={() => Linking.openURL(supportPhoneLink)}
              />
            ) : null}
          </Footer>

          {showVideo && (
            <HelpVideo
              onVideoEnd={() => setShowVideo(false)}
              videoURI="https://d3ag5cip9ldvh3.cloudfront.net/PRIMEIRACOMPRA.MP4"
            />
          )}

          <SupportTypeBottomSheet
            isOpen={showSupportBottomSheet}
            onClose={() => setShowSupportBottomSheet(false)}
          />
        </Container>
      </TouchableWithoutFeedback>
    </SafeAreaView>
  );
}
